import { normalizeText } from '@vif/core';
import { assertByteLimit, finalizeDocument } from './limits.js';
/** File imports gate on bytes BEFORE decoding (PRD §8.5, Codex F1). */
function decodeFileBytes(data) {
    assertByteLimit(data.byteLength);
    return new TextDecoder('utf-8').decode(data);
}
/** CRLF / lone CR → LF, before any structural work. */
export function normalizeLineEndings(text) {
    return text.replace(/\r\n?/g, '\n');
}
function collapseWhitespace(text) {
    return text.replace(/\s+/g, ' ').trim();
}
/**
 * Plain text: paragraphs split on blank lines (prototype behavior),
 * single newlines are soft wraps joined with a space.
 */
export function parsePlainText(text) {
    const clean = normalizeText(normalizeLineEndings(text));
    const blocks = [];
    for (const rawParagraph of clean.split(/\n\s*\n/)) {
        const paragraph = collapseWhitespace(rawParagraph);
        if (paragraph.length > 0)
            blocks.push({ kind: 'paragraph', text: paragraph });
    }
    return finalizeDocument(blocks);
}
/**
 * Canonical file-import surface for .txt (Codex F1): the 50 MB byte gate
 * runs before the bytes are ever decoded. Shells route FILES through this,
 * never through the string API.
 */
export function parsePlainTextBytes(data) {
    return parsePlainText(decodeFileBytes(data));
}
/** Canonical file-import surface for .md — see parsePlainTextBytes. */
export function parseMarkdownBytes(data) {
    return parseMarkdown(decodeFileBytes(data));
}
/**
 * Paste is plain text plus explicit line-ending + NFC normalization (P1
 * brief). Intentionally a string API with NO 50 MB check: the byte limit is
 * a file concept and the clipboard hands us a string (Codex F1); the 2 M
 * word limit still applies via finalizeDocument.
 */
export function parsePaste(text) {
    return parsePlainText(text);
}
const ATX_HEADING = /^(#{1,6})\s+(.*)$/;
/**
 * Markdown-lite: ATX headings become chapter-heading blocks; everything else
 * is plain paragraphs. No other markdown syntax is interpreted — inline
 * markers are read as-is (cut line: no rich rendering).
 */
export function parseMarkdown(text) {
    const clean = normalizeText(normalizeLineEndings(text));
    const blocks = [];
    let paragraphLines = [];
    const flushParagraph = () => {
        const paragraph = collapseWhitespace(paragraphLines.join(' '));
        paragraphLines = [];
        if (paragraph.length > 0)
            blocks.push({ kind: 'paragraph', text: paragraph });
    };
    for (const line of clean.split('\n')) {
        const heading = ATX_HEADING.exec(line);
        if (heading !== null) {
            flushParagraph();
            // CommonMark closing sequence: trailing #s (space-separated) are not content.
            const title = collapseWhitespace((heading[2] ?? '').replace(/\s+#+\s*$/, ''));
            if (title.length > 0) {
                blocks.push({
                    kind: 'chapter-heading',
                    text: title,
                    title,
                    level: (heading[1] ?? '#').length,
                });
            }
            continue;
        }
        if (line.trim().length === 0) {
            flushParagraph();
            continue;
        }
        paragraphLines.push(line);
    }
    flushParagraph();
    return finalizeDocument(blocks);
}
//# sourceMappingURL=text.js.map